import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { sanitize } from "@/lib/sanitize";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import {
  Package, Plus, Trash, PencilSimple, MagnifyingGlass, X, Image as ImageIcon,
  FileText, Tag, WhatsappLogo, ArrowLeft, CaretDown, CaretUp, Clock, Share,
  Eye, CurrencyDollar, Barcode, SquaresFour, ListBullets, GearSix, ChartBar,
  Folders, DownloadSimple, UploadSimple, Timer, CheckCircle
} from "@phosphor-icons/react";

const fmtM = (n) => n ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2 })}` : "$0.00";

const CATEGORIES = ["Codificadores", "Impresoras", "Empaques", "UV", "Accesorios", "Promociones"];

export default function ProductsModule({ readOnly = false }) {
  const { user, token } = useAuth();
  const canEdit = !readOnly && (user?.role === "admin" || user?.role === "auxiliar" || user?.role === "subgerente");
  const isAdmin = canEdit;
  const canViewTable = user?.role === "admin" || user?.role === "auxiliar" || user?.role === "subgerente";
  const [view, setView] = useState(canViewTable ? "table" : "catalog");
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(17.50);
  const headers = { Authorization: `Bearer ${token}` };

  const [form, setForm] = useState({
    sku: "", name: "", description: "", category_path: "", brand: "",
    stock_qty: 0, stock_prev: 0, track_inventory: true,
    costo: 0, margin_percent: 0,
    precio_lista: 0, precio_promocion: 0, precio_sin_costo: 0,
    variants: [], specs: [],
    promotion: null
  });

  const loadProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (catFilter) params.append("category", catFilter);
      if (stockFilter) params.append("stock_filter", stockFilter);
      params.append("limit", "100");
      const { data } = await axios.get(`${API}/products?${params}`, { headers });
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token, search, catFilter, stockFilter]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    axios.get(`${API}/products/config/exchange-rate`, { headers })
      .then(r => setExchangeRate(r.data.rate))
      .catch(() => {});
  }, [token]);

  // Load first image thumbnail for each product in catalog view
  const [thumbUrls, setThumbUrls] = useState({});
  useEffect(() => {
    if (view !== "catalog") return;
    products.forEach(p => {
      if (!p.images?.length || thumbUrls[p.id]) return;
      const img = p.images[0];
      axios.get(`${API}/products/files/${img.storage_path}`, { headers, responseType: "blob" })
        .then(r => {
          const url = URL.createObjectURL(r.data);
          setThumbUrls(prev => ({ ...prev, [p.id]: url }));
        })
        .catch(() => {});
    });
  }, [products, view]);

  const resetForm = () => {
    setForm({ sku: "", name: "", description: "", category_path: "", brand: "", stock_qty: 0, stock_prev: 0, track_inventory: true, costo: 0, margin_percent: 0, precio_lista: 0, precio_promocion: 0, precio_sin_costo: 0, variants: [], specs: [], promotion: null });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU y nombre requeridos"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/products/${editingId}`, form, { headers });
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/products`, form, { headers });
        toast.success("Producto creado");
      }
      resetForm(); loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      sku: p.sku || "", name: p.name || "", description: p.description || "",
      category_path: p.category_path || "", brand: p.brand || "",
      stock_qty: p.stock_qty || 0, stock_prev: p.stock_prev || 0, track_inventory: p.track_inventory !== false,
      costo: p.costo || 0, margin_percent: p.margin_percent || 0,
      precio_lista: p.precio_lista || 0, precio_promocion: p.precio_promocion || 0,
      precio_sin_costo: p.precio_sin_costo || 0,
      variants: p.variants || [], specs: p.specs || [],
      promotion: p.promotion || null
    });
    setShowForm(true);
    setSelectedProduct(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este producto?")) return;
    try { await axios.delete(`${API}/products/${id}`, { headers }); toast.success("Eliminado"); loadProducts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleShare = async (product, hours) => {
    try {
      const { data } = await axios.post(`${API}/products/${product.id}/share`, { hours }, { headers });
      const url = `${window.location.origin}/producto/${data.id}`;
      const text = `*${product.name}*\nSKU: ${product.sku}\nPL: ${fmtM(product.precio_lista)}\n\nVer: ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      toast.success("Link creado");
    } catch { toast.error("Error al compartir"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/products/import`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`Importado: ${data.created} creados, ${data.updated} actualizados`);
      if (data.errors?.length > 0) toast.warning(`${data.errors.length} errores`);
      loadProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar"); }
    e.target.value = "";
  };

  // Company config (logo)
  const [showConfig, setShowConfig] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products/config/company`, { headers }).then(r => setCompanyName(r.data.name || "")).catch(() => {});
    axios.get(`${API}/products/config/company/logo-file`, { headers, responseType: "blob" }).then(r => setCompanyLogoUrl(URL.createObjectURL(r.data))).catch(() => {});
  }, [token]);

  const handleSaveCompany = async () => {
    try { await axios.put(`${API}/products/config/company`, { name: companyName }, { headers }); toast.success("Nombre actualizado"); } catch { toast.error("Error"); }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/products/config/company/logo`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      // Reload logo
      const r = await axios.get(`${API}/products/config/company/logo-file`, { headers, responseType: "blob" });
      setCompanyLogoUrl(URL.createObjectURL(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    e.target.value = "";
  };

  // Detail View
  if (selectedProduct) {
    return <ProductDetail product={selectedProduct} onBack={() => setSelectedProduct(null)} onEdit={handleEdit} onShare={handleShare} isAdmin={isAdmin} user={user} />;
  }

  // Group products by brand for catalog view
  const grouped = {};
  products.forEach(p => {
    const b = p.brand || "Sin marca";
    if (!grouped[b]) grouped[b] = [];
    grouped[b].push(p);
  });

  return (
    <div className="space-y-5" data-testid="products-module">
      {/* View Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package size={28} weight="fill" className="text-blue-600" /> Productos
          </h1>
          <span className="text-sm text-slate-400">{total} productos</span>
          <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full font-mono font-bold">TC: {exchangeRate.toFixed(4)}</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex bg-slate-100 p-1 rounded-lg">
            {canViewTable && <button onClick={() => setView("table")} className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 ${view === "table" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="view-table"><ListBullets size={14} /> Tabla</button>}
            <button onClick={() => setView("catalog")} className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 ${view === "catalog" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="view-catalog"><SquaresFour size={14} /> Catalogo</button>
          </div>
          {isAdmin && (
            <label className="cursor-pointer">
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-file-input" />
              <span className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"><UploadSimple size={14} /> Importar</span>
            </label>
          )}
          {isAdmin && (
            <button onClick={async () => {
              try {
                const res = await axios.get(`${API}/products/import-template`, { headers, responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a"); a.href = url; a.download = "Plantilla_Productos.xlsx";
                document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
              } catch { toast.error("Error al descargar plantilla"); }
            }} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-emerald-300 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors" data-testid="download-template-btn">
              <DownloadSimple size={14} /> Plantilla
            </button>
          )}
          {isAdmin && <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-product-btn"><Plus size={14} className="mr-1" /> Nuevo Producto</Button>}
          {isAdmin && <button onClick={() => setShowConfig(!showConfig)} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500" title="Configuracion Empresa" data-testid="company-config-btn"><GearSix size={16} /></button>}
        </div>
      </div>

      {/* Company Config */}
      {showConfig && isAdmin && (
        <div className="bg-slate-50 border rounded-xl p-5 space-y-3" data-testid="company-config-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-700">Configuracion de Empresa</p>
            <button onClick={() => setShowConfig(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center overflow-hidden">
              {companyLogoUrl ? <img src={companyLogoUrl} alt="Logo" className="w-full h-full object-contain" /> : <Package size={24} className="text-slate-300" />}
            </div>
            <div className="space-y-2">
              <label className="cursor-pointer text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 inline-flex items-center gap-1">
                <UploadSimple size={14} /> Subir Logo
                <input type="file" accept="image/*" onChange={handleUploadLogo} className="hidden" data-testid="upload-logo-input" />
              </label>
              <p className="text-[10px] text-slate-400">JPG, PNG o SVG. Max 5MB. Se usa en links compartidos por WhatsApp.</p>
            </div>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Nombre de la Empresa</Label>
              <Input value={companyName} onChange={e => setCompanyName(e.target.value)} className="h-9 text-sm mt-1" placeholder="STI Codificadores" data-testid="company-name-input" />
            </div>
            <Button size="sm" onClick={handleSaveCompany} data-testid="save-company-btn">Guardar</Button>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[250px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="SKU, nombre o descripcion..." className="pl-9 h-10 text-sm" data-testid="product-search" />
        </div>
        <select value={stockFilter} onChange={e => setStockFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white min-w-[140px]" data-testid="stock-filter">
          <option value="">Todos los stocks</option>
          <option value="in_stock">En stock</option>
          <option value="out_of_stock">Sin stock</option>
        </select>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white min-w-[160px]" data-testid="category-filter">
          <option value="">Todas las categorias</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Create/Edit Form */}
      {showForm && isAdmin && (
        <div className="bg-slate-50 border rounded-xl p-5 space-y-4" data-testid="product-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-700">{editingId ? "Editar Producto" : "Nuevo Producto"}</p>
            <button onClick={resetForm} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label className="text-xs">SKU *</Label><Input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} className="h-9 text-sm mt-1 font-mono" data-testid="product-sku" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Nombre *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 text-sm mt-1" data-testid="product-name" /></div>
            <div><Label className="text-xs">Marca</Label><Input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} className="h-9 text-sm mt-1" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Categoria (ej: ANSER · CODIFICADORES)</Label><Input value={form.category_path} onChange={e => setForm({...form, category_path: e.target.value})} className="h-9 text-sm mt-1" /></div>
          </div>
          <div>
            <Label className="text-xs">Descripcion</Label>
            <div className="mt-1">
              <RichTextEditor value={form.description} onChange={v => setForm({...form, description: v})} placeholder="Descripcion detallada del producto..." compact minHeight="100px" testId="product-description-editor" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="flex items-center gap-3 col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.track_inventory} onChange={e => setForm({...form, track_inventory: e.target.checked})} className="w-4 h-4 rounded" data-testid="track-inventory-toggle" />
                <span className="text-xs font-semibold text-slate-700">Controla Inventario</span>
              </label>
              <span className="text-[10px] text-slate-400">{form.track_inventory ? "El stock se descuenta al vender" : "Sin control de stock (servicios, comodin)"}</span>
            </div>
            {form.track_inventory && (
            <div><Label className="text-xs">Stock Actual</Label><Input type="number" value={form.stock_qty} onChange={e => setForm({...form, stock_qty: parseInt(e.target.value)||0})} className="h-9 text-sm mt-1" /></div>
            )}
            {form.track_inventory && (
            <div><Label className="text-xs">Stock Anterior</Label><Input type="number" value={form.stock_prev} onChange={e => setForm({...form, stock_prev: parseInt(e.target.value)||0})} className="h-9 text-sm mt-1" /></div>
            )}
            <div><Label className="text-xs">Costo (Compra)</Label><Input type="number" step="0.01" value={form.costo} onChange={e => { const c = parseFloat(e.target.value)||0; const pl = form.margin_percent > 0 ? Math.round(c * (1 + form.margin_percent/100)*100)/100 : form.precio_lista; setForm({...form, costo: c, precio_lista: pl}); }} className="h-9 text-sm mt-1" data-testid="product-costo" /></div>
            <div><Label className="text-xs">Margen %</Label><Input type="number" step="0.1" value={form.margin_percent} onChange={e => { const m = parseFloat(e.target.value)||0; const pl = form.costo > 0 ? Math.round(form.costo * (1 + m/100)*100)/100 : form.precio_lista; setForm({...form, margin_percent: m, precio_lista: pl}); }} className="h-9 text-sm mt-1" data-testid="product-margin" /></div>
            <div><Label className="text-xs">PL (Precio Lista)</Label><Input type="number" step="0.01" value={form.precio_lista} onChange={e => { const pl = parseFloat(e.target.value)||0; const m = form.costo > 0 ? Math.round((pl/form.costo - 1)*10000)/100 : 0; setForm({...form, precio_lista: pl, margin_percent: m}); }} className="h-9 text-sm mt-1" data-testid="product-pl" /></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div><Label className="text-xs">PPR (Precio Promo)</Label><Input type="number" step="0.01" value={form.precio_promocion} onChange={e => setForm({...form, precio_promocion: parseFloat(e.target.value)||0})} className="h-9 text-sm mt-1" /></div>
            <div><Label className="text-xs">PSC (Sin Costo)</Label><Input type="number" step="0.01" value={form.precio_sin_costo} onChange={e => setForm({...form, precio_sin_costo: parseFloat(e.target.value)||0})} className="h-9 text-sm mt-1" /></div>
            {form.costo > 0 && form.precio_lista > 0 && (
              <div className="col-span-3 flex items-center gap-3 bg-emerald-50 rounded-lg px-3 py-1.5">
                <span className="text-xs text-emerald-700 font-semibold">Utilidad: ${Math.round((form.precio_lista - form.costo)*100)/100} ({Math.round((form.precio_lista/form.costo - 1)*10000)/100}%)</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} data-testid="product-save">{editingId ? "Guardar Cambios" : "Crear Producto"}</Button>
            <Button size="sm" variant="outline" onClick={resetForm}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* TABLE VIEW */}
      {view === "table" && (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="products-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">SKU</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-4 py-3 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">Stock</th>
                  {!readOnly && <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Costo</th>}
                  <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">PL</th>
                  {!readOnly && <th className="px-4 py-3 text-center text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Margen</th>}
                  <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">PPR</th>
                  {!readOnly && <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.length === 0 ? (
                  <tr><td colSpan={readOnly ? 5 : 8} className="py-16 text-center text-slate-400">
                    <Package size={40} className="mx-auto mb-2 text-slate-300" />
                    Sin productos. Clic "+ Nuevo Producto"
                  </td></tr>
                ) : products.map(p => {
                  const costo = p.costo || 0;
                  const pl = p.precio_lista || 0;
                  const marginPct = costo > 0 ? Math.round((pl / costo - 1) * 10000) / 100 : (p.margin_percent || 0);
                  const utilidad = costo > 0 ? Math.round((pl - costo) * 100) / 100 : 0;
                  return (
                    <tr key={p.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedProduct(p)} data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-3"><span className="text-blue-600 font-mono text-xs font-semibold bg-blue-50 px-2 py-0.5 rounded">{p.sku}</span></td>
                      <td className="px-4 py-3 font-semibold text-slate-800 max-w-[200px] truncate">{p.name}</td>
                      <td className="px-4 py-3 text-center">
                        {p.track_inventory === false
                          ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">S/I</span>
                          : p.stock_qty > 0
                          ? <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{p.stock_qty}</span>
                          : <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">0</span>
                        }
                      </td>
                      {!readOnly && <td className="px-4 py-3 text-right text-xs text-slate-500 font-mono">{costo > 0 ? fmtM(costo) : "-"}</td>}
                      <td className="px-4 py-3 text-right text-xs font-semibold font-mono">{fmtM(pl)}</td>
                      {!readOnly && (
                      <td className="px-4 py-3 text-center">
                        {costo > 0 && pl > 0 ? (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${marginPct >= 30 ? "bg-emerald-100 text-emerald-700" : marginPct >= 15 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"}`}>
                            {marginPct}% (${utilidad})
                          </span>
                        ) : <span className="text-[10px] text-slate-400">-</span>}
                      </td>
                      )}
                      <td className="px-4 py-3 text-right text-xs font-bold text-emerald-600 font-mono">{fmtM(p.precio_promocion)}</td>
                      {!readOnly && (
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {isAdmin && <button onClick={() => handleEdit(p)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-500 flex items-center justify-center transition-colors" data-testid={`edit-product-${p.id}`}><PencilSimple size={13} /></button>}
                          {user?.role === "admin" && <button onClick={() => handleDelete(p.id)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-red-600 hover:text-white text-slate-400 flex items-center justify-center transition-colors" data-testid={`delete-product-${p.id}`}><Trash size={13} /></button>}
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CATALOG VIEW */}
      {view === "catalog" && (
        <div className="space-y-6" data-testid="products-catalog">
          {/* Category Pills */}
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setCatFilter("")} className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${!catFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-600"}`}>Todas</button>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCatFilter(catFilter === c ? "" : c)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${catFilter === c ? "bg-slate-900 text-white border-slate-900" : c === "Promociones" ? "text-red-500 border-red-200 hover:border-red-400 bg-white" : "bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-600"}`}
                data-testid={`cat-pill-${c}`}>{c}</button>
            ))}
          </div>

          {products.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border">
              <Package size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">Sin productos</p>
            </div>
          ) : (
            Object.entries(grouped).map(([brand, prods]) => (
              <div key={brand}>
                <div className="flex items-center gap-3 mb-4 bg-white border rounded-xl px-5 py-3" data-testid={`brand-group-${brand}`}>
                  <span className="font-bold text-blue-600 text-lg">{brand}</span>
                  <span className="text-sm text-slate-400">· {prods.length} productos</span>
                  {prods[0]?.category_path && <span className="text-xs bg-slate-100 text-slate-500 px-3 py-1 rounded-full">{prods[0].category_path.split(" · ").pop()}</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                  {prods.map(p => (
                    <div key={p.id} onClick={() => setSelectedProduct(p)}
                      className="bg-white border rounded-2xl overflow-hidden cursor-pointer hover:border-blue-300 hover:-translate-y-1 transition-all duration-300 hover:shadow-lg group"
                      data-testid={`product-card-${p.id}`}>
                      {/* Image */}
                      <div className="relative aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden">
                        {thumbUrls[p.id]
                          ? <img src={thumbUrls[p.id]} alt={p.name} className="w-full h-full object-cover" />
                          : <Package size={40} className="text-slate-300" />
                        }
                        {/* Stock badge */}
                        <span className="absolute top-2.5 right-2.5">
                          {p.track_inventory === false
                            ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-500 text-white">S/I</span>
                            : p.stock_qty > 0
                            ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white">{p.stock_qty} OK</span>
                            : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">Sin stock</span>
                          }
                        </span>
                        {/* Promo badge */}
                        {p.promotion?.active && (
                          <span className="absolute top-2.5 left-2.5 text-[10px] font-bold px-2 py-0.5 rounded-md bg-red-500 text-white">-{p.promotion.discount_pct}% PROMO</span>
                        )}
                        {/* Image count */}
                        {(p.images||[]).length > 0 && (
                          <span className="absolute bottom-2 left-2.5 text-[10px] bg-black/50 text-white px-2 py-0.5 rounded backdrop-blur-sm"><ImageIcon size={10} className="inline mr-0.5" /> {p.images.length}</span>
                        )}
                      </div>
                      {/* Body */}
                      <div className="p-4">
                        <p className="text-blue-600 font-mono text-[11px] font-semibold mb-1">{p.sku}</p>
                        <p className="font-bold text-sm text-slate-800 mb-3 line-clamp-2 min-h-[40px]">{p.name}</p>
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-lg font-extrabold text-emerald-600">{fmtM(p.precio_lista)}</p>
                            {p.precio_sin_costo > 0 && <p className="text-xs text-slate-400 line-through">{fmtM(p.precio_sin_costo)}</p>}
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100">
                          <div className="flex gap-3 text-slate-400 text-[10px]">
                            <span className="flex items-center gap-1"><ImageIcon size={10} /> {(p.images||[]).length}</span>
                            <span className="flex items-center gap-1"><FileText size={10} /> {(p.documents||[]).length}</span>
                          </div>
                          <button onClick={e => { e.stopPropagation(); handleShare(p, 5); }} className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full hover:bg-green-100 flex items-center gap-1 transition-colors">
                            <WhatsappLogo size={12} weight="fill" /> Compartir
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// =================== PRODUCT DETAIL ===================
function ProductDetail({ product, onBack, onEdit, onShare, isAdmin, user }) {
  const { token } = useAuth();
  const [tab, setTab] = useState("descripcion");
  const [shareHours, setShareHours] = useState(5);
  const [p, setP] = useState(product);
  const [selectedImg, setSelectedImg] = useState(0);
  const [imgUrls, setImgUrls] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  // Load image blobs
  useEffect(() => {
    if (!p.images?.length) return;
    p.images.forEach(img => {
      if (imgUrls[img.id]) return;
      axios.get(`${API}/products/files/${img.storage_path}`, { headers, responseType: "blob" })
        .then(r => {
          const url = URL.createObjectURL(r.data);
          setImgUrls(prev => ({ ...prev, [img.id]: url }));
        })
        .catch(() => {});
    });
    return () => { Object.values(imgUrls).forEach(u => URL.revokeObjectURL(u)); };
  }, [p.images]);

  const reloadProduct = async () => {
    try {
      const { data } = await axios.get(`${API}/products/${p.id}`, { headers });
      setP(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error("Error reloading product:", e); }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/products/${p.id}/images/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Imagen subida");
      reloadProduct();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    e.target.value = "";
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/products/${p.id}/documents/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido");
      reloadProduct();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    e.target.value = "";
  };

  const handleDeleteImage = async (imgId) => {
    if (!window.confirm("Eliminar imagen?")) return;
    await axios.delete(`${API}/products/${p.id}/images/${imgId}`, { headers });
    reloadProduct();
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Eliminar documento?")) return;
    await axios.delete(`${API}/products/${p.id}/documents/${docId}`, { headers });
    reloadProduct();
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const { data } = await axios.get(`${API}/products/files/${doc.storage_path}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const promoEnd = p.promotion?.end_date ? new Date(p.promotion.end_date) : null;
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0 });

  useEffect(() => {
    if (!promoEnd) return;
    const tick = () => {
      const diff = promoEnd - new Date();
      if (diff <= 0) { setCountdown({ d: 0, h: 0, m: 0 }); return; }
      setCountdown({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000)
      });
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => clearInterval(iv);
  }, [promoEnd]);

  const TABS = [
    { id: "descripcion", label: "Descripcion" },
    { id: "specs", label: "Especificaciones" },
    { id: "ficha", label: "Ficha Tecnica" },
    { id: "manuales", label: "Manuales" },
    { id: "precios", label: "Precios" },
    { id: "promos", label: "Promos" },
  ];

  return (
    <div className="space-y-6" data-testid="product-detail">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="product-back">
        <ArrowLeft size={16} /> Volver al catalogo
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/3] max-h-[400px] bg-slate-100 border rounded-2xl flex items-center justify-center mb-3 overflow-hidden">
            {p.images?.length > 0 && imgUrls[p.images[selectedImg]?.id]
              ? <img src={imgUrls[p.images[selectedImg]?.id]} alt={p.name} className="w-full h-full object-contain" />
              : <Package size={64} className="text-slate-300" />
            }
          </div>
          <div className="flex gap-2.5 flex-wrap items-center">
            {(p.images || []).map((img, i) => (
              <div key={img.id} onClick={() => setSelectedImg(i)}
                className={`relative w-20 h-16 bg-slate-100 border rounded-xl flex items-center justify-center cursor-pointer transition-colors overflow-hidden ${i === selectedImg ? "border-blue-400 ring-2 ring-blue-200" : "hover:border-slate-300"}`}>
                {imgUrls[img.id] ? <img src={imgUrls[img.id]} alt="" className="w-full h-full object-cover" /> : <Package size={16} className="text-slate-300" />}
                {user?.role === "admin" && (
                  <button onClick={e => { e.stopPropagation(); handleDeleteImage(img.id); }} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:opacity-100"><X size={8} /></button>
                )}
              </div>
            ))}
            {isAdmin && (
              <label className="w-20 h-16 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors">
                <input type="file" accept="image/*" onChange={handleUploadImage} className="hidden" />
                <Plus size={18} className="text-slate-400" />
              </label>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="space-y-5">
          <div>
            <span className="text-blue-600 font-mono text-sm font-semibold bg-blue-50 px-2.5 py-1 rounded">{p.sku}</span>
            {p.track_inventory === false
              ? <span className="ml-2 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">Sin inventario</span>
              : p.stock_qty > 0
              ? <span className="ml-2 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{p.stock_qty} en stock</span>
              : <span className="ml-2 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">Sin stock</span>
            }
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900">{p.name}</h2>
          {p.description && <div className="text-sm text-slate-500 leading-relaxed prose prose-sm max-w-none overflow-hidden break-words [&_*]:max-w-full" dangerouslySetInnerHTML={sanitize(p.description)} />}

          {/* Pricing Cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 border rounded-xl p-4 text-center">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">PSC</p>
              <p className="text-xl font-extrabold text-slate-500">{fmtM(p.precio_sin_costo)}</p>
            </div>
            <div className="bg-slate-50 border rounded-xl p-4 text-center">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">PL</p>
              <p className="text-xl font-extrabold text-slate-900">{fmtM(p.precio_lista)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-1">PPR</p>
              <p className="text-xl font-extrabold text-emerald-700">{fmtM(p.precio_promocion)}</p>
            </div>
          </div>

          {/* Variants */}
          {(p.variants || []).length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Variantes de venta</p>
              <div className="flex gap-2 flex-wrap">
                {p.variants.map((v, i) => (
                  <span key={i} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer transition-colors ${i === 0 ? "border-blue-400 text-blue-600 bg-blue-50" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{v.name || v}</span>
                ))}
              </div>
            </div>
          )}

          {/* Promotion Banner */}
          {p.promotion?.active && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="promo-banner">
              <div className="flex items-center gap-3">
                <span className="bg-red-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-wide">PROMO</span>
                <span className="text-sm font-semibold text-red-700">{p.promotion.description || `${p.promotion.discount_pct}% descuento`}</span>
                <span className="text-2xl font-extrabold text-red-500">-{p.promotion.discount_pct}%</span>
              </div>
              {promoEnd && (
                <div className="flex gap-2">
                  {[{v: countdown.d, l: "Dias"}, {v: countdown.h, l: "Hrs"}, {v: countdown.m, l: "Min"}].map(u => (
                    <div key={u.l} className="bg-white border rounded-lg px-3 py-2 text-center min-w-[48px]">
                      <p className="font-mono text-lg font-extrabold text-emerald-600">{String(u.v).padStart(2, "0")}</p>
                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">{u.l}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2.5 flex-wrap">
            <button onClick={() => onShare(p, shareHours)} className="bg-green-500 hover:bg-green-600 text-white font-semibold text-sm px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors" data-testid="share-whatsapp">
              <WhatsappLogo size={18} weight="fill" /> Compartir por WhatsApp
            </button>
            <button onClick={() => {
              const ficha = (p.documents || []).find(d => d.doc_type === "ficha_tecnica" || d.doc_type === "datasheet");
              if (ficha) { handleDownloadDoc(ficha); } else { toast.info("Sin ficha tecnica. Sube una desde la pestaña 'Ficha Tecnica'."); setTab("ficha"); }
            }} className="bg-white border text-slate-600 hover:border-blue-400 hover:text-blue-600 font-medium text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors" data-testid="btn-ficha"><FileText size={16} /> Ver Ficha Tecnica</button>
            <button onClick={() => {
              const manual = (p.documents || []).find(d => d.doc_type === "manual");
              if (manual) { handleDownloadDoc(manual); } else { toast.info("Sin manual. Sube uno desde la pestaña 'Manuales'."); setTab("manuales"); }
            }} className="bg-white border text-slate-600 hover:border-blue-400 hover:text-blue-600 font-medium text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors" data-testid="btn-manual"><DownloadSimple size={16} /> Ver Manual</button>
            {isAdmin && <button onClick={() => onEdit(p)} className="bg-white border text-slate-600 hover:border-blue-400 hover:text-blue-600 font-medium text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors"><PencilSimple size={16} /> Editar</button>}
          </div>

          {/* WhatsApp expiration + link tracking */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Link expira en:</span>
            {[{h: 1, l: "1 hora"}, {h: 5, l: "5 horas"}, {h: 24, l: "24 horas"}, {h: 0, l: "Sin exp."}].map(o => (
              <button key={o.h} onClick={() => setShareHours(o.h)} className={`px-3 py-1 rounded-lg text-[11px] font-bold border transition-colors ${shareHours === o.h ? "border-green-400 text-green-600 bg-green-50" : "border-slate-200 text-slate-400 hover:border-slate-300"}`}>{o.l}</button>
            ))}
          </div>

          {/* Share Links Tracking */}
          <ShareLinksTracker productId={p.id} token={token} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs font-semibold rounded-md transition-colors ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            data-testid={`detail-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white border rounded-xl p-6 min-h-[200px]">
        {tab === "descripcion" && (
          <div className="prose prose-sm max-w-none text-slate-600">
            <h3 className="text-base font-bold text-slate-800 mb-3">Descripcion Completa</h3>
            {p.description ? <div dangerouslySetInnerHTML={sanitize(p.description)} /> : <p className="text-slate-400">Sin descripcion disponible.</p>}
          </div>
        )}
        {tab === "specs" && (
          <div>
            <h3 className="text-base font-bold text-slate-800 mb-3">Especificaciones Tecnicas</h3>
            {(p.specs || []).length > 0 ? (
              <div className="divide-y">
                {p.specs.map((s, i) => (
                  <div key={i} className="flex justify-between py-2.5 text-sm">
                    <span className="text-slate-500 font-medium">{s.key}</span>
                    <span className="text-slate-800 font-semibold">{s.value}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">Sin especificaciones registradas.</p>}
          </div>
        )}
        {tab === "ficha" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-slate-800">Fichas Tecnicas</h3>
              {isAdmin && <label className="cursor-pointer text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 flex items-center gap-1"><UploadSimple size={14} /> Subir<input type="file" onChange={handleUploadDoc} className="hidden" /></label>}
            </div>
            {(p.documents || []).filter(d => d.doc_type === "ficha_tecnica" || d.doc_type === "datasheet").length > 0 ? (
              <div className="space-y-2">
                {p.documents.filter(d => d.doc_type === "ficha_tecnica" || d.doc_type === "datasheet").map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 border rounded-lg">
                    <div className="flex items-center gap-3"><FileText size={20} className="text-blue-500" /><div><p className="text-sm font-medium text-slate-700">{d.original_filename}</p><p className="text-[10px] text-slate-400">{(d.size / 1024).toFixed(0)} KB</p></div></div>
                    <div className="flex gap-2">
                      <button onClick={() => handleDownloadDoc(d)} className="text-xs text-blue-600 hover:underline">Descargar</button>
                      {user?.role === "admin" && <button onClick={() => handleDeleteDoc(d.id)} className="text-xs text-red-400 hover:text-red-600">Eliminar</button>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">Sin fichas tecnicas. {isAdmin ? "Sube una desde el boton arriba." : ""}</p>}
          </div>
        )}
        {tab === "manuales" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-slate-800">Manuales</h3>
              {isAdmin && <label className="cursor-pointer text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 flex items-center gap-1"><UploadSimple size={14} /> Subir<input type="file" onChange={handleUploadDoc} className="hidden" /></label>}
            </div>
            {(p.documents || []).filter(d => d.doc_type === "manual").length > 0 ? (
              <div className="space-y-2">
                {p.documents.filter(d => d.doc_type === "manual").map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 border rounded-lg">
                    <div className="flex items-center gap-3"><FileText size={20} className="text-emerald-500" /><div><p className="text-sm font-medium text-slate-700">{d.original_filename}</p><p className="text-[10px] text-slate-400">{(d.size / 1024).toFixed(0)} KB</p></div></div>
                    <div className="flex gap-2">
                      <button onClick={() => handleDownloadDoc(d)} className="text-xs text-blue-600 hover:underline">Descargar</button>
                      {user?.role === "admin" && <button onClick={() => handleDeleteDoc(d.id)} className="text-xs text-red-400 hover:text-red-600">Eliminar</button>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">Sin manuales. {isAdmin ? "Sube uno desde el boton arriba." : ""}</p>}
          </div>
        )}
        {tab === "precios" && (
          <div className="space-y-4">
            <h3 className="text-base font-bold text-slate-800 mb-3">Estructura de Precios</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border rounded-xl p-5 text-center">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">PSC (Precio Sin Costo)</p>
                <p className="text-3xl font-extrabold text-slate-500">{fmtM(p.precio_sin_costo)}</p>
              </div>
              <div className="border rounded-xl p-5 text-center">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">PL (Precio de Lista)</p>
                <p className="text-3xl font-extrabold text-slate-900">{fmtM(p.precio_lista)}</p>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-5 text-center">
                <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">PPR (Precio Promocion)</p>
                <p className="text-3xl font-extrabold text-emerald-700">{fmtM(p.precio_promocion)}</p>
              </div>
            </div>
          </div>
        )}
        {tab === "promos" && (
          <div>
            <h3 className="text-base font-bold text-slate-800 mb-3">Promociones Activas</h3>
            {p.promotion?.active ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <p className="font-bold text-red-700 text-lg">-{p.promotion.discount_pct}% de descuento</p>
                <p className="text-sm text-red-600 mt-1">{p.promotion.description}</p>
                {p.promotion.end_date && <p className="text-xs text-red-400 mt-2">Valido hasta: {new Date(p.promotion.end_date).toLocaleDateString("es-MX")}</p>}
              </div>
            ) : <p className="text-sm text-slate-400">Sin promociones activas.</p>}
          </div>
        )}
      </div>
    </div>
  );
}


// =================== SHARE LINKS TRACKER ===================
function ShareLinksTracker({ productId, token }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/products/share/links`, { headers })
      .then(r => setLinks((r.data || []).filter(l => l.product_id === productId)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId, token]);

  if (loading || links.length === 0) return null;

  const isExpired = (link) => {
    if (!link.expires_at) return false;
    return new Date() > new Date(link.expires_at);
  };

  return (
    <div className="border rounded-xl p-3 bg-slate-50" data-testid="share-links-tracker">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Links compartidos ({links.length})</p>
      <div className="space-y-2 max-h-[150px] overflow-y-auto">
        {links.map(link => (
          <div key={link.id} className={`flex items-center justify-between text-xs p-2 rounded-lg border ${isExpired(link) ? "bg-red-50 border-red-200" : link.opened ? "bg-green-50 border-green-200" : "bg-white border-slate-200"}`}>
            <div className="flex items-center gap-2 min-w-0">
              {link.opened ? (
                <Eye size={14} className="text-green-600 shrink-0" />
              ) : isExpired(link) ? (
                <Clock size={14} className="text-red-400 shrink-0" />
              ) : (
                <Clock size={14} className="text-slate-400 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-semibold text-slate-700 truncate">
                  {link.hours ? `${link.hours}h` : "Sin exp."} · {new Date(link.created_at).toLocaleDateString("es-MX")}
                </p>
                {link.opened && link.opened_at && (
                  <p className="text-[10px] text-green-600 font-semibold">Visto: {new Date(link.opened_at).toLocaleString("es-MX")}</p>
                )}
              </div>
            </div>
            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
              isExpired(link) ? "bg-red-100 text-red-600" : link.opened ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
            }`}>
              {isExpired(link) ? "Expirado" : link.opened ? "Visto" : "Pendiente"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
